import React from 'react';
import DocumentMeta from 'react-document-meta';
import { Link } from 'react-router-dom';
import MarkdownContent from '../components/MarkdownContent';
import BlogSection from '../components/BlogSection';
import blogPosts from '../data/blogPosts';
import { useI18n } from '../i18n/I18nContext';
import styles from '../styles/SinglePost.module.css';

const publicUrl = process.env.PUBLIC_URL || '';

const postDefinitions = {
  overview: {
    slugs: { en: 'sourdough', de: 'sauerteig', bg: 'kvas' },
    titleKey: 'pages.blogPost.overviewTitle',
    introKey: 'pages.blogPost.overviewIntro',
    contentFile: 'why-sourdough-needs-time',
    image: `${publicUrl}/images/breads/dinkel-pure.jpg`,
    imageAlt: 'Homemade spelt sourdough bread',
    articleImage: `${publicUrl}/images/breads/dinkel-bread2.webp`,
    articleImageAlt: 'Freshly baked artisan bread',
    publishedAt: '2026-09-16',
    contents: [
      { id: 'overview-more-than-trend', labelKey: 'pages.blogPost.overviewContentsTrend' },
      { id: 'overview-what-is-sourdough', labelKey: 'pages.blogPost.overviewContentsWhat' },
      { id: 'overview-benefits', labelKey: 'pages.blogPost.overviewContentsBenefits' },
      { id: 'overview-crust', labelKey: 'pages.blogPost.overviewContentsCrust' },
    ],
  },
  basics: {
    slugs: { en: 'what-is-sourdough', de: 'was-ist-sauerteig', bg: 'kakvo-e-kvas' },
    titleKey: 'pages.blogPost.basicsTitle',
    introKey: 'pages.blogPost.basicsIntro',
    contentFile: 'what-is-sourdough',
    image: `${publicUrl}/images/breads/dinkel-fruits2.jpg`,
    imageAlt: 'Sliced homemade sourdough bread',
    articleImage: `${publicUrl}/images/breads/dinkel-bread2.webp`,
    articleImageAlt: 'Freshly baked artisan bread',
    publishedAt: '2026-09-17',
    contents: [
      { id: 'basics-what-is-sourdough', labelKey: 'pages.blogPost.contentsBasicsWhat' },
      { id: 'basics-more-than-trend', labelKey: 'pages.blogPost.contentsBasicsTrend' },
    ],
  },
  benefits: {
    slugs: { en: 'sourdough-benefits', de: 'sauerteig-vorteile', bg: 'predimstva-na-kvasa' },
    titleKey: 'pages.blogPost.benefitsTitle',
    introKey: 'pages.blogPost.benefitsIntro',
    contentFile: 'sourdough-benefits',
    image: `${publicUrl}/images/breads/dinkel-kerne.webp`,
    imageAlt: 'Wholegrain sourdough bread',
    articleImage: `${publicUrl}/images/breads/dinkel-bread1.webp`,
    articleImageAlt: 'Artisan sourdough bread',
    publishedAt: '2026-09-18',
    contents: [
      { id: 'benefits-character', labelKey: 'pages.blogPost.contentsBenefitsCharacter' },
    ],
  },
  process: {
    slugs: {
      en: 'from-starter-to-crust',
      de: 'vom-anstellgut-bis-zur-kruste',
      bg: 'ot-kvasa-do-korichkata',
    },
    titleKey: 'pages.blogPost.processTitle',
    introKey: 'pages.blogPost.processIntro',
    contentFile: 'from-starter-to-crust',
    image: `${publicUrl}/images/breads/bread-slice.webp`,
    imageAlt: 'Fresh bread with a golden crust',
    articleImage: `${publicUrl}/images/breads/dinkel-bread1.webp`,
    articleImageAlt: 'Hand-shaped artisan bread',
    publishedAt: '2026-09-19',
    contents: [
      { id: 'process-patience', labelKey: 'pages.blogPost.contentsProcessPatience' },
      { id: 'process-bake', labelKey: 'pages.blogPost.contentsProcessBake' },
    ],
  },
};

const posts = Object.keys(postDefinitions).reduce((allPosts, definitionKey) => {
  const definition = postDefinitions[definitionKey];
  Object.values(definition.slugs).forEach(slug => {
    allPosts[slug] = definition;
  });
  return allPosts;
}, {
  'sourdough-needs-time': postDefinitions.overview,
  'fermentation-creates-character': postDefinitions.basics,
  'warum-sauerteig-zeit-braucht': postDefinitions.overview,
});

const SinglePost = ({ match }) => {
  const { language, t } = useI18n();
  const post = posts[match.params.slug];

  if (!post) {
    return (
      <main className={styles.page}>
        <section className={styles.notFound}>
          <h1>{t('pages.blogPost.notFound')}</h1>
          <Link to={`${publicUrl}/Blog`}>{t('pages.blogPost.back')}</Link>
        </section>
      </main>
    );
  }

  const title = t(post.titleKey);
  const intro = t(post.introKey);
  const contentSrc = `${publicUrl}/content/sourdough/${language}/${post.contentFile}.md`;
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareUrl = encodeURIComponent(pageUrl);
  const shareText = encodeURIComponent(`${title} | Grain Bites`);
  const dateLocale = { en: 'en-GB', de: 'de-DE', bg: 'bg-BG' }[language] || 'en-GB';
  const publishedDate = new Intl.DateTimeFormat(dateLocale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${post.publishedAt}T12:00:00`));
  const contents = post.contents.map(item => ({
    ...item,
    label: t(item.labelKey),
  }));
  const categories = [
    { to: `${publicUrl}/Artisan-Breads`, label: t('pages.blogPost.categoryBread') },
    { to: `${publicUrl}/Sourdough`, label: t('pages.blogPost.categorySourdough') },
    { to: `${publicUrl}/Tea`, label: t('pages.blogPost.categoryTea') },
    { to: `${publicUrl}/Menu`, label: t('pages.blogPost.categoryMenu') },
  ];
  const meta = {
    title: `${title} | Grain Bites`,
    description: intro,
    meta: {
      name: {
        description: intro,
        keywords: 'sourdough, artisan bread, homemade bread, Grain Bites journal',
        robots: 'index, follow',
        author: 'Irena Popova',
        'twitter:card': 'summary_large_image',
        'twitter:title': title,
        'twitter:description': intro,
        'twitter:image': post.articleImage,
      },
      property: {
        'og:type': 'article',
        'og:title': title,
        'og:description': intro,
        'og:image': post.articleImage,
        'og:url': pageUrl,
        'article:published_time': post.publishedAt,
        'article:author': 'Irena Popova',
      },
    },
  };

  return (
    <DocumentMeta {...meta} extend>
      <main className={styles.page}>
        <header
          className={styles.hero}
          style={{ backgroundImage: `url('${post.image}')` }}
        >
          <p className={styles.label}>{t('pages.blogPost.label')}</p>
          <time className={styles.date} dateTime={post.publishedAt}>
            {t('pages.blogPost.published')} · {publishedDate}
          </time>
          <h1>{title}</h1>
          <p>{intro}</p>
        </header>

        <article className={styles.article}>
          <div className={styles.articleGrid}>
            <div className={styles.mainColumn}>
              <img className={styles.image} src={post.articleImage} alt={post.articleImageAlt} />
              <MarkdownContent
                src={contentSrc}
                className={styles.markdown}
                headingIds={['post-title', ...contents.map(item => item.id)]}
              />
            </div>

            <aside className={styles.sidebar} aria-label="Explore Grain Bites">
              <div className={styles.profile}>
                <img className={styles.profileImage} src={post.image} alt="Fresh Grain Bites bread" />
                <p className={styles.profileName}>Grain Bites</p>
                <p className={styles.profileText}>
                  Homemade bread, tea, and good food made with time and care.
                </p>
                <Link className={styles.profileLink} to={`${publicUrl}/About`}>
                  Discover our story
                </Link>
              </div>

              <div className={styles.explore}>
                <p className={styles.sidebarLabel}>Journal</p>
                <h2>Explore more</h2>
                <nav className={styles.sidebarNav} aria-label="Related pages">
                  <Link to={`${publicUrl}/Artisan-Breads`}>Artisan Bread</Link>
                  <Link to={`${publicUrl}/Tea`}>Tea &amp; moments</Link>
                  <Link to={`${publicUrl}/Menu`}>The menu</Link>
                  <Link to={`${publicUrl}/Blog`}>All journal stories</Link>
                </nav>
              </div>

              <div className={styles.contents}>
                <p className={styles.sidebarLabel}>Contents</p>
                <nav className={styles.contentsNav} aria-label="Article contents">
                  {contents.map(item => (
                    <a href={`#${item.id}`} key={item.id}>{item.label}</a>
                  ))}
                </nav>
              </div>

              <div className={styles.categories}>
                <p className={styles.sidebarLabel}>Categories</p>
                <nav className={styles.categoryNav} aria-label="Post categories">
                  {categories.map(category => (
                    <Link to={category.to} key={category.to}>{category.label}</Link>
                  ))}
                </nav>
              </div>

              <div className={styles.share}>
                <p>Share</p>
                <div className={styles.shareLinks}>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Share on Facebook"
                  >
                    f
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Share on X"
                  >
                    𝕏
                  </a>
                  <a
                    href={`mailto:?subject=${shareText}&body=${shareUrl}`}
                    aria-label="Share by email"
                  >
                    @
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </article>

        <BlogSection
          eyebrow="Journal"
          title="Stories from Grain Bites"
          posts={blogPosts}
          moreLabel="View more stories"
          moreTo={`${publicUrl}/Blog`}
        />

        <Link className={styles.back} to={`${publicUrl}/Blog`}>
          {t('pages.blogPost.back')}
        </Link>
      </main>
    </DocumentMeta>
  );
};

export default SinglePost;
